import { get } from 'lodash';
import { type IFramework } from '@/framework';
import { getLocaleStore } from '@/i18n';
import { LayerType, type ILayerProps } from '@/version-types';
import {
    DEFAULT_PREVENT_HIDDEN_TYPES,
    GROUP_ITEM_BOTTOM_DECORATION_HEIGHT,
    ITEM_HEIGHT,
    ITEM_MARGIN_BOTTOM_GROUPED,
    ITEM_MARGIN_BOTTOM_NORMAL,
} from './constants';

export const getRenderLayers = (layers: ILayerProps[], options = { isChildren: false }) => {
    const { isChildren } = options;

    if (isChildren) {
        return { layers: layers.toReversed(), backgroundLayer: null, mainLayer: null };
    }

    const backgroundLayer = layers.find((layer) => layer.layerType === LayerType.BACKGROUND_LAYER);
    const mainLayer = layers.find((layer) => layer.layerType === LayerType.MAIN_LAYER);
    const renderLayers = layers.filter((layer) => !DEFAULT_PREVENT_HIDDEN_TYPES.includes(layer.layerType)).toReversed();

    return { layers: renderLayers, backgroundLayer: backgroundLayer || null, mainLayer: mainLayer || null };
};

export const getLayerPath = (layer: ILayerProps, framework: IFramework) => {
    if (!layer.parentId) return [layer.id];

    const path: string[] = [layer.id];

    let parentId = layer.parentId;
    while (parentId) {
        path.push(parentId);

        const parentLayer = framework.getLayer(parentId);
        if (!parentLayer) break;

        if (parentLayer.parentId) {
            parentId = parentLayer.parentId;
        } else {
            break;
        }
    }

    return path.toReversed();
};

export const getDroppableContentHeight = (layers: ILayerProps[]) => {
    let height = layers.reduce((acc, layer, idx) => {
        const marginBottom =
            idx === layers.length - 1
                ? 0
                : layer.layerType === LayerType.GROUP_LAYER
                  ? ITEM_MARGIN_BOTTOM_GROUPED
                  : ITEM_MARGIN_BOTTOM_NORMAL;
        const itemTotalHeight = ITEM_HEIGHT + marginBottom;
        return acc + itemTotalHeight;
    }, 0);

    // 如果最后一个图层是组图层，需要加上组图层 item 的底部装饰高度，防止未满最大高度时出现滚动条
    if (layers.length > 0 && layers[layers.length - 1].layerType === LayerType.GROUP_LAYER) {
        height += GROUP_ITEM_BOTTOM_DECORATION_HEIGHT;
    }

    return height;
};

export const getLayerTitle = (layer: ILayerProps) => {
    const { $t, locale } = getLocaleStore();

    if (layer.title && locale !== 'en' && layer.layerType !== LayerType.TEXT_LAYER) return layer.title;

    const layerType = layer.layerType;

    switch (layerType) {
        case LayerType.GROUP_LAYER:
            return $t((v) => v.plugin.hierarchy.groupLayer);
        case LayerType.BACKGROUND_LAYER:
            return $t((v) => v.plugin.hierarchy.backgroundLayer);
        case LayerType.MAIN_LAYER:
            return $t((v) => v.plugin.hierarchy.mainLayer);
        case LayerType.TEXT_LAYER:
            return get(layer, 'data.text', '');
        case LayerType.VECTOR_LAYER:
            return $t((v) => v.plugin.hierarchy.vectorLayer);
        case LayerType.BRUSH_LAYER:
        case LayerType.MARKUP_LAYER:
            return $t((v) => v.plugin.hierarchy.draw);
        case LayerType.TABLE:
            return $t((v) => v.plugin.hierarchy.tableLayer);
        case LayerType.CHART:
            return $t((v) => v.plugin.hierarchy.chartLayer);
        case LayerType.IMAGE_LAYER:
            return $t((v) => v.plugin.hierarchy.imageLayer);
        case LayerType.QR_CODE:
            return $t((v) => v.plugin.hierarchy.qrcodeLayer);
        case LayerType.WATERMARK:
            return $t((v) => v.plugin.hierarchy.watermarkLayer);
        default:
            return $t((v) => v.plugin.hierarchy.layer);
    }
};
